import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Dashboard, Dictionary, Group, Identifier, KeyValuePair, TagDefinition, WidgetValues } from '../../models';
import { guidEmpty } from '../../common/constants';
import { toDictionary } from '../../common/helpers';
import { MapWidget } from './map.widget';
import { getMarkers } from './getMarkers';
import { MarkerDefinition } from './MarkerDefinition';
import { IDateRangeService } from '../../services/general';
import { IDashboardHelperService } from '@services/helpers';
import { MapGroupViewModel } from './MapGroupViewModel';
import { getIdentifiersDictionaryForGroupsOnMap } from './getIdentifiersDictionaryForGroupsOnMap';

interface LeafletArgs {
    leafletObject: { togglePopup: () => void };
    model: MarkerDefinition;
}

/**
 * Map component based on leaflet and ui-leaflet
 * https://angular-ui.github.io/ui-leaflet/examples/0000-viewer.html#/basic/first-example
 */
class MapController implements angular.IComponentController {
    center: {
        lat: number;
        lng: number;
        zoom: number;
    } = { lat: 0, lng: 0, zoom: 4 };
    defaults = { attributionControl: true, zoomControl: true };
    events = {
        markers: {
            enable: ['mouseover', 'mouseout', 'click']
        }
    };
    id: string;
    identifiersByGroup: Dictionary<Identifier[]>;
    isLoading: boolean;
    markers: {};

    private _garbages: (() => void)[] = [];

    readonly boundData: WidgetValues;
    readonly dashboards: Dashboard[];
    readonly groups: Group[];
    readonly item: MapWidget;
    readonly selectedGroup: Group;
    readonly tags: Dictionary<TagDefinition>;

    static $inject = ['$scope', '$state', 'dateRangeService', 'dashboardHelperService'];
    constructor(
        private _$scope: angular.IScope,
        private _$state: angular.ui.IStateService,
        private _dateRangeService: IDateRangeService,
        private _dashboardHelperService: IDashboardHelperService
    ) {}

    $onInit() {
        this.identifiersByGroup = getIdentifiersDictionaryForGroupsOnMap(this.groups);
        this.setCenter();

        this.id = `leaflet-map-${this.item.guid}`;
        this._garbages.push(
            this._$scope.$on('leafletDirectiveMarker.' + this.id + '.mouseover', (_event, args: LeafletArgs) => {
                args.leafletObject.togglePopup();
            })
        );
        this._garbages.push(
            this._$scope.$on('leafletDirectiveMarker.' + this.id + '.mouseout', (_event, args: LeafletArgs) => {
                args.leafletObject.togglePopup();
            })
        );
        this._garbages.push(
            this._$scope.$on('leafletDirectiveMarker.' + this.id + '.click', (_event, args: LeafletArgs) => {
                if (this.item.dashboardLink && this.item.dashboardLink !== guidEmpty) {
                    if (args.model.guid === this.selectedGroup.guid) {
                        this.setCenter();
                    } else {
                        this.gotoDestination(args.model.guid);
                    }
                }
            })
        );

        // trick to render the map correctly
        setTimeout(function () {
            window.dispatchEvent(new Event('resize'));
        }, 200);
    }

    $onChanges(changesObj: angular.IOnChangesObject): void {
        if (changesObj['groups']?.currentValue) {
            this.identifiersByGroup = getIdentifiersDictionaryForGroupsOnMap(this.groups);
        }
        this.markers = getMarkers(this.tags, this.item, this.getGroupsWithAlarmsDictionary(this.groups, this.boundData?.alarms || []));
        if (this.selectedGroup) {
            this.setCenter();
        }
    }

    $onDestroy() {
        this._garbages.forEach((garbage) => garbage());
    }

    private setCenter() {
        this.center =
            this.selectedGroup?.customProperties && this.selectedGroup.customProperties['latitude'] && this.selectedGroup.customProperties['longitude']
                ? {
                      ...this.item.configuration.center,
                      lat: parseFloat(this.selectedGroup.customProperties['latitude']),
                      lng: parseFloat(this.selectedGroup.customProperties['longitude'])
                  }
                : this.item.configuration.center;
        // Crappy way to save settings
        this.item.configuration.center = this.center;
    }

    private getGroupsWithAlarmsDictionary(groups: Group[], alarms: KeyValuePair<string, number>[]) {
        const groupDictionary: Dictionary<MapGroupViewModel> = {};
        if (groups) {
            groups.forEach((group) => {
                if (alarms && this.identifiersByGroup && group.guid in this.identifiersByGroup) {
                    const identifiersInGroupGuid = this.identifiersByGroup[group.guid].map((identifier) => identifier.guid);
                    const alarmsForGroup = alarms.filter((alarm) => identifiersInGroupGuid.includes(alarm.key)).map((alarm) => alarm.value - 1);
                    let worstAlarmLevel = _.max(alarmsForGroup);
                    worstAlarmLevel = worstAlarmLevel >= 0 ? worstAlarmLevel : undefined;
                    groupDictionary[group.guid] = { group, alarmLevel: worstAlarmLevel };
                } else {
                    groupDictionary[group.guid] = { group, alarmLevel: undefined };
                }
            });
        }
        return groupDictionary;
    }

    private gotoDestination(guid: string): void {
        if (guid && guid !== guidEmpty) {
            const selectedGuid = guid;
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            const go = this._dashboardHelperService.getGotoDestination(
                this.item.dashboardLink,
                toDictionary(this.dashboards),
                selectedGuid,
                beginDate,
                endDate
            );
            this._$state.go(go.route, go.params);
        }
    }
}

export const wtMap: ComponentDefinition = {
    componentId: 'wtMap',
    controller: MapController,
    controllerAs: 'vm',
    template: require('./map.component.html'),
    bindings: {
        boundData: '<',
        dashboards: '<',
        groups: '<',
        item: '<',
        selectedGroup: '<',
        tags: '<'
    }
};
